import { Bidder } from '../../enums'
import {
  IBidRequest,
  IS2SParams,
  IValidBidRequest,
  BidAdapter,
  IBidderRequest,
  IBidResponse
} from '../../../typings/IPrebid'
import { registerBidder } from 'prebid.js/src/adapters/bidderFactory'
import * as loadjs from 'loadjs'
import { Cookie } from '../../helpers/cookie'
import { Device } from '../../helpers'
import Logger from '../../helpers/Logger'
import { Protocol } from '../../enums/Protocol'
import { getS2SBeta } from './BetaTest'
const log = (...messages) => Logger.custom('s2s', ...messages)

export class S2SAdapter implements BidAdapter {
  private readonly bidRequestUrl: string
  private readonly supportedMediaTypes = ['banner', 'video']

  constructor() {
    this.bidRequestUrl = getBidRequestUrl()
  }

  get uuid() {
    if (window.$mediavine.web.hash) {
      return window.$mediavine.web.hash
    }

    const tokensCookie = new Cookie<IMVTokens>('mv_tokens')

    try {
      return tokensCookie.value.mv_uuid
    } catch (e) {
      return ''
    }
  }

  code = Bidder.S2S
  buildRequests(
    validBidRequests: Array<IValidBidRequest<IS2SParams>>,
    bidderRequest: IBidderRequest
  ): { data: IBidRequestMetadata; url: string; method: 'POST' } {
    const data: IBidRequestMetadata = {
      uuid: this.uuid,
      device: collectDeviceMetadata(),
      referrer: document.referrer,
      gdpr: window.$mediavine.web.model.gdpr || '0',
      slug: window.$mediavine.web.model.slug,
      categories: window.$mediavine.web.model.categories,
      secure: document.location.protocol === Protocol.https ? '1' : '0',
      eids: getEIDs()
    }

    log(`request data`, data)

    return {
      data: { ...data, ...bidderRequest },
      url: this.bidRequestUrl,
      method: 'POST'
    }
  }

  interpretResponse(
    { body }: { body: Array<object> },
    request
  ): Array<IBidResponse> {
    const bidResponses: Array<IBidResponse & IResponseMetadata> = []
    body.forEach((rtbResponse: IRTBResponse & IResponseMetadata) => {
      rtbResponse.seatbid.forEach((seatbid) => {
        seatbid.bid.forEach((bid) => {
          try {
            const bidResponse: IBidResponse & IResponseMetadata = {} as any
            if (!bid.adm) {
              return
            }
            bidResponse.s2sBidder = rtbResponse.bidder
            bidResponse.requestId = bid.impid
            bidResponse.ad = bid.adm
            bidResponse.creativeId = bid.crid
            bidResponse.cpm = bid.price
            bidResponse.unadjustedCpm = bid.unadjustedPrice || bid.price
            bidResponse.height = bid.h
            bidResponse.width = bid.w
            bidResponse.currency = 'USD'
            bidResponse.ttl = bid.exp
            bidResponse.netRevenue = true
            bidResponse.nurl = bid.nurl
            bidResponse.mediaType = rtbResponse.mediaType

            if (rtbResponse.mediaType === 'video') {
              bidResponse.vastXml = bid.adm
            }

            bidResponses.push(bidResponse)
          } catch (e) {
            log('Error building response', e, rtbResponse)
          }
        })
      })
    })

    return bidResponses
  }

  getUserSyncs(syncOptions, serverResponses) {}
  isBidRequestValid() {
    return true
  }
  onBidWon() {}
  onSetTargeting() {}
  onTimeout() {}
}

const spec = new S2SAdapter()
registerBidder(spec)

function collectDeviceMetadata(): IDeviceMetadata {
  return {
    os: Device.os,
    osv: Device.osversion,
    dnt:
      navigator.doNotTrack == 'yes' ||
      navigator.doNotTrack == '1' ||
      navigator.msDoNotTrack == '1'
        ? 1
        : 0,
    h: screen.height,
    w: screen.width,
    ua: navigator.userAgent,
    language: navigator.language
  }
}

function buildDigitrustEid({ id, keyv }): IExternalId {
  return {
    source: 'digitru.st',
    uids: [
      {
        id,
        ext: {
          keyv
        }
      }
    ]
  }
}

function buildTdEid(id): IExternalId {
  return {
    source: 'adserver.org',
    uids: [
      {
        id,
        ext: {
          rtiPartner: 'TDID'
        }
      }
    ]
  }
}

function getEIDs(): Array<IExternalId> {
  const eids: Array<IExternalId> = []
  const prebidUserIds = window.pbjs.getUserIds()

  Object.keys(prebidUserIds).forEach((key) => {
    switch (key) {
      case 'digitrustid':
        const { id, keyv } = prebidUserIds[key].data
        if (!id || !keyv) {
          return
        }

        eids.push(buildDigitrustEid({ id, keyv }))
        break

      case 'tdid':
        eids.push(buildTdEid(prebidUserIds[key]))
        break

      default:
        break
    }
  })

  return eids
}

/**
 * Interpolates the beta value onto bid request URL.
 */
function getBidRequestUrl(): string {
  const beta = getS2SBeta()
  // register targeting
  window.googletag = window.googletag || {}
  window.googletag.cmd = window.googletag.cmd || []
  window.googletag.cmd.push(() => {
    const pubads = window.googletag.pubads()
    pubads.setTargeting('s2sVersion', beta.name)
  })

  return `${process.env.S2S_URL}/bidRequest/?s2sVersion=${beta.name}`
}

interface IRTBResponse<extension = {}> {
  bidder: string
  /**
   * ID of the bid request to which this is a response.
   */
  id: string
  /**
   * Array of seatbid objects; 1+ required if a bid is to be made.
   */
  seatbid: Array<ISeatbid>
  /** Bidder generated response ID to assist with logging/tracking. */
  bidid?: string
  /** Bid currency using ISO-4217 alpha codes. */
  cur?: string
  /**
   * Optional feature to allow a bidder to set data in the
   * exchange’s cookie. The string must be in base85 cookie safe
   * characters and be in any format. Proper JSON encoding must
   * be used to include “escaped” quotation marks.
   */
  customdata?: string
  /** Reason for not bidding. Refer to List 5.24. */
  npr?: number
  /** Placeholder for bidder-specific extensions to OpenRTB. */
  ext?: extension
}

interface ISeatbid<extension = {}> {
  /**
   * Array of 1+ Bid objects (Section 4.2.3) each related to an impression.
   * Multiple bids can relate to the same impression.
   */
  bid: Array<IBid>
  /**
   * ID of the buyer seat (e.g., advertiser, agency) on whose behalf
   * this bid is made.
   */
  seat?: string
  /**
   * default 0
   * 0 = impressions can be won individually.
   * 1 = impressions must be won or lost as a group.
   */

  group?: number
  /** Placeholder for bidder-specific extensions to OpenRTB. */

  ext?: extension
}

interface IBid<extension = {}> {
  /**
   * Bidder generated bid ID to assist with logging/tracking.
   */
  id: string
  /* required ID of the Imp object in the related bid request. */
  impid: string
  /** required Bid price expressed as CPM although the actual transaction is
   * for a unit impression only. Note that while the type indicates
   * float, integer math is highly recommended when handling
   * currencies (e.g., BigDecimal in Java).
   */
  price: number

  /** Metadata for original price prior to discrepancy adjustment */
  unadjustedPrice?: number
  /** Win notice URL called by the exchange if the bid wins (not
   * necessarily indicative of a delivered, viewed, or billable ad);
   * optional means of serving ad markup. Substitution macros
   * (Section 4.4) may be included in both the URL and optionally
   * returned markup.
   */
  nurl?: string
  /**
   * Billing notice URL called by the exchange when a winning bid
   * becomes billable based on exchange-specific business policy
   * (e.g., typically delivered, viewed, etc.). Substitution macros
   * (Section 4.4) may be included.
   */
  burl?: string
  /**
   * Loss notice URL called by the exchange when a bid is known to
   * have been lost. Substitution macros (Section 4.4) may be included.
   * Exchange-specific policy may preclude support for loss notices
   * or the disclosure of winning clearing prices resulting in ${AUCTION_PRICE}
   * macros being removed (i.e., replaced with a zero-length string).
   */
  lurl?: string
  /**
   * Means of conveying ad markup in case the bid wins; supersedes
   * the win notice if markup is included in both. Substitution macros
   * (Section 4.4) may be included.
   */
  adm?: string
  /** ID of a preloaded ad to be served if the bid wins. */
  adid: string
  /**
   * Advertiser domain for block list checking (e.g., “ford.com”).
   * This can be an array of for the case of rotating creatives.
   * Exchanges can mandate that only one domain is allowed.
   */
  adomain?: Array<string>
  /**
   * A platform-specific application identifier intended to be unique
   * to the app and independent of the exchange. On Android, this
   * should be a bundle or package name (e.g., com.foo.mygame). On
   * iOS, it is a numeric ID.
   */
  bundle: string
  /**
   * URL without cache-busting to an image that is representative of
   * the content of the campaign for ad quality/safety checking.
   */
  iurl: string
  /**
   * Campaign ID to assist with ad quality checking; the collection
   * of creatives for which iurl should be representative.
   */
  cid: string
  /** Creative ID to assist with ad quality checking. */
  crid: string
  /**
   * Tactic ID to enable buyers to label bids for reporting to the
   * exchange the tactic through which their bid was submitted. The
   * specific usage and meaning of the tactic ID should be communicated
   * between buyer and exchanges a priori.
   */
  tactic: string
  /** IAB content categories of the creative. Refer to List 5.1. */
  cat: Array<string>
  /** Set of attributes describing the creative. Refer to List 5.3. */
  attr: Array<number>
  /** API required by the markup if applicable. Refer to List 5.6. */
  api: number
  /** Video response protocol of the markup if applicable. Refer to List 5.8. */
  protocol: number
  /** Creative media rating per IQG guidelines. Refer to List 5.19. */
  qagmediarating: number
  /**
   * Language of the creative using ISO-639-1-alpha-2. The nonstandard
   * code “xx” may also be used if the creative has no linguistic content
   * (e.g., a banner with just a company logo).
   */
  language: string
  /**
   * Reference to the deal.id from the bid request if this bid pertains
   * to a private marketplace direct deal.
   */
  dealid: string
  /** Width of the creative in device independent pixels (DIPS). */
  w: number
  /** Height of the creative in device independent pixels (DIPS). */
  h: number
  /** Relative width of the creative when expressing size as a ratio. Required for Flex Ads. */
  wratio: number
  /**
   * Relative height of the creative when expressing size as a ratio.
   * Required for Flex Ads.
   */
  hratio: number
  /**
   * Advisory as to the number of seconds the bidder is willing to
   * wait between the auction and the actual impression.
   */
  exp: number
  /** Placeholder for bidder-specific extensions to OpenRTB */
  ext: extension
}

interface IResponseMetadata {
  unadjustedCpm: number
  netRevenue: boolean
  nurl?: string
  mediaType: string
}

interface IMVTokens {
  mv_uuid: string
}

interface IBidRequestMetadata {
  uuid: string
  device: IDeviceMetadata
  gdpr: '1' | '0'
  referrer: string
  slug: string
  secure: '1' | '0'
  categories: Array<ICategories>
  eids: Array<IExternalId>
}

interface IExternalId {
  source: string
  uids: Array<{
    id: string
    ext: {
      [key: string]: any
    }
  }>
}

interface IDeviceMetadata {
  make?: string
  model?: string
  os?: string
  osv?: string
  dnt?: number
  language?: string
  devicetype?: string
  h?: number
  w?: number
  ua?: string
}
